import { useAuth0 } from '@auth0/auth0-react';
import { useEffect, type ReactNode } from 'react';
import { useTenantConfig } from '@/tenant/TenantConfigProvider';
import { resetRestartLoginGuard } from '@/auth/restartLoginGuard';

/**
 * Surfaces Auth0 SDK errors that were previously swallowed (the SDK sets
 * `error` and the app silently restart-looped into a blank page).
 *
 * Two cases:
 *  - access_denied "user … is not part of the … organization": expected
 *    tenancy behaviour (e.g. an Etcho user opening a Bamboo host). Universal
 *    Login can't refuse before the code is sent — org membership is only
 *    evaluated after authentication (also anti-enumeration) — so the denial
 *    lands back here and deserves a human screen, not a loop.
 *  - anything else (failed code exchange, invalid state, …): show the real
 *    error so it can be reported, with a clean retry.
 */
export function AuthErrorGate({ children }: { children: ReactNode }) {
    const { error, logout, loginWithRedirect } = useAuth0();
    const tenantConfig = useTenantConfig();

    useEffect(() => {
        if (error) {
            // Deliberately loud: this error was previously invisible.
            console.error('[auth0] authentication error:', error);
        }
    }, [error]);

    if (!error) {
        return <>{children}</>;
    }

    const tenantName = tenantConfig?.name ?? 'this workspace';
    const brandColor = tenantConfig?.branding?.colors?.primary ?? '#000f68';
    const isOrgDenial =
        /is not part of the .* organization/i.test(error.message ?? '') ||
        ('error' in error && error.error === 'access_denied');

    const retry = () => {
        // Clear the Auth0 session first so retrying offers a fresh identifier
        // prompt rather than silently re-approving the same wrong account.
        resetRestartLoginGuard();
        logout({ logoutParams: { returnTo: window.location.origin } });
    };

    return (
        <div
            style={{
                minHeight: '100vh',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontFamily: 'Helvetica, Arial, sans-serif',
                padding: 24,
            }}
        >
            <div style={{ maxWidth: 420, textAlign: 'center' }}>
                {tenantConfig?.branding?.logoUrl && (
                    <img
                        src={tenantConfig.branding.logoUrl}
                        alt={tenantName}
                        style={{ maxHeight: 48, marginBottom: 24 }}
                    />
                )}
                <h1 style={{ fontSize: 20, marginBottom: 12 }}>
                    {isOrgDenial
                        ? `This account doesn't have access to ${tenantName}`
                        : 'There was a problem signing you in'}
                </h1>
                <p style={{ color: '#555', fontSize: 15, lineHeight: 1.5 }}>
                    {isOrgDenial
                        ? 'The email address you signed in with is not a member of this workspace. Try a different account, or contact your administrator for access.'
                        : (error.message ?? String(error))}
                </p>
                <button
                    onClick={isOrgDenial ? retry : () => loginWithRedirect()}
                    style={{
                        marginTop: 20,
                        padding: '12px 24px',
                        fontSize: 15,
                        fontWeight: 600,
                        color: '#fff',
                        backgroundColor: brandColor,
                        border: 'none',
                        borderRadius: 6,
                        cursor: 'pointer',
                    }}
                >
                    {isOrgDenial ? 'Sign in with a different account' : 'Try again'}
                </button>
            </div>
        </div>
    );
}
